import { Box, ButtonBase, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { getMyCards } from "../../../services/cards";
import { Skeleton } from "../../shared";
import { CardVisual } from "./CardVisual";

interface MyCardsSectionProps {
  isLoading?: boolean;
}

/**
 * Ограничение ширины отдельной карты — иначе на десктопе одна карта
 * растянется на пол-экрана. 360px — компромисс: сохраняем aspect-ratio
 * 500:295 и получаем комфортный «телефонный» размер.
 */
const MAX_CARD_WIDTH = "360px";

export function MyCardsSection({ isLoading: parentLoading }: MyCardsSectionProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("cards");
  const { data, isLoading: queryLoading } = useQuery({
    queryFn: getMyCards,
    queryKey: ["my-cards"],
    // Пока есть хотя бы одна карта в статусе processing (Airwallex ждёт webhook
    // `issuing.card.active`), поллим список раз в 5 секунд — так юзер увидит
    // активацию без ручного refresh.
    refetchInterval: (list) =>
      Array.isArray(list) && list.some((c) => c.status === "processing") ? 5000 : false,
  });
  const cards = data ?? [];
  const isLoading = parentLoading || queryLoading;

  return (
    <Box
      sx={{
        display: "grid",
        gap: "12px",
        // На мобиле — стек по одной карте на строку (форм-фактор Telegram
        // WebApp). На десктопе-планшете (>= 600px) — сетка в 2 колонки,
        // но каждая карта ограничена по ширине и прижимается влево, а не
        // растягивается на всю колонку.
        gridTemplateColumns: {
          xs: "1fr",
          sm: `repeat(2, minmax(0, ${MAX_CARD_WIDTH}))`,
        },
        justifyContent: { xs: "stretch", sm: "flex-start" },
      }}
    >
      {isLoading ? (
        <>
          <Skeleton
            variant="rounded"
            sx={{ borderRadius: "20px", aspectRatio: "500 / 295" }}
          />
          <Skeleton
            variant="rounded"
            sx={{ borderRadius: "20px", aspectRatio: "500 / 295" }}
          />
        </>
      ) : cards.length === 0 ? (
        <Box
          sx={{
            padding: "24px 16px",
            borderRadius: "18px",
            bgcolor: "secondary.main",
            textAlign: "center",
            gridColumn: { xs: "1", sm: "1 / -1" },
          }}
        >
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {t("my_cards_empty")}
          </Typography>
        </Box>
      ) : (
        cards.map((card) => (
          <ButtonBase
            key={card.id}
            onClick={() => navigate(`/cards/${card.id}`)}
            sx={{
              display: "block",
              textAlign: "left",
              borderRadius: "20px",
              transition: "transform 120ms ease",
              "&:active": { transform: "scale(0.985)" },
            }}
          >
            <CardVisual card={card} />
          </ButtonBase>
        ))
      )}
    </Box>
  );
}
